import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { auth, ApiClientError, type Account } from '../lib/api';

/**
 * Session state for the whole app.
 *
 * The server is the source of truth: on mount we call `/auth/me` and let a 401
 * mean "signed out". Nothing about the session is cached in localStorage, so
 * revoking a session server-side takes effect on the next request.
 */
interface AuthState {
  account: Account | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setAccount(await auth.me());
    } catch (error) {
      if (error instanceof ApiClientError && error.isUnauthenticated) {
        setAccount(null);
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    setAccount(await auth.login(email, password));
  }, []);

  const logout = useCallback(async () => {
    await auth.logout();
    setAccount(null);
  }, []);

  const value = useMemo(
    () => ({ account, loading, login, logout, refresh }),
    [account, loading, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}
